import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { diffFields, hasChanges } from '@obraplan/core/domain/audit/audit-log';
import { DEFAULT_RISK_HORIZON_DAYS } from '@obraplan/core/domain/calendar/constants';
import { DEFAULT_MEASUREMENT_MODE } from '@obraplan/core/domain/planning/team-measurement';
import { normalizeProjectCode, type Project } from '@obraplan/core/domain/structure/project';
import { asId, type ProjectId } from '@obraplan/core/domain/shared/ids';
import { err, ok, type Result } from '@obraplan/core/domain/shared/result';
import { conflictError, notFoundError, type AppError } from '../../errors/app-error';
import { ERROR_CODE, ERROR_MESSAGE } from '../../errors/error-codes';
import { createProjectSchema, updateProjectSchema } from '../../contracts/structure';
import { parseInput } from '../../contracts/parse';
import type { Clock } from '../../ports/clock';
import type { IdGenerator } from '../../ports/id-generator';
import {
  SETTINGS_KEY,
  type ProjectRepository,
  type SettingsRepository,
} from '../../ports/repositories';
import type { TransactionRunner } from '../../ports/transaction';
import type { AuditService } from '../../services/audit-service';
import type { PermissionChecker } from '../../services/permission-checker';
import type { Session } from '../../session';

export class CreateProject {
  constructor(
    private readonly projects: ProjectRepository,
    private readonly permissions: PermissionChecker,
    private readonly audit: AuditService,
    private readonly tx: TransactionRunner,
    private readonly clock: Clock,
    private readonly ids: IdGenerator,
  ) {}

  async execute(input: unknown, session: Session): Promise<Result<Project, AppError>> {
    const allowed = this.permissions.ensure(session, PERMISSION.PROJECTS_MANAGE);
    if (!allowed.ok) return allowed;

    const parsed = parseInput(createProjectSchema, input);
    if (!parsed.ok) return parsed;

    return this.tx.run(async () => {
      const normalizedCode = normalizeProjectCode(parsed.value.code);
      const existing = await this.projects.findByNormalizedCode(normalizedCode);
      if (existing !== undefined) {
        return err(
          conflictError(
            ERROR_CODE.PROJECT_CODE_TAKEN,
            ERROR_MESSAGE[ERROR_CODE.PROJECT_CODE_TAKEN]!,
            { code: parsed.value.code },
          ),
        );
      }

      const now = this.clock.nowIso();
      const project: Project = {
        id: this.ids.next<'Project'>(),
        code: parsed.value.code,
        name: parsed.value.name,
        timezone: parsed.value.timezone,
        status: 'PLANNING',
        riskHorizonDays: DEFAULT_RISK_HORIZON_DAYS,
        requireDistinctInspector: false,
        measurementMode: parsed.value.measurementMode ?? DEFAULT_MEASUREMENT_MODE,
        createdAt: now,
        updatedAt: now,
        ...(parsed.value.externalId === undefined ? {} : { externalId: parsed.value.externalId }),
        ...(parsed.value.client === undefined ? {} : { client: parsed.value.client }),
        ...(parsed.value.address === undefined ? {} : { address: parsed.value.address }),
        ...(parsed.value.technicalManager === undefined
          ? {}
          : { technicalManager: parsed.value.technicalManager }),
        ...(parsed.value.startDate === undefined ? {} : { startDate: parsed.value.startDate }),
        ...(parsed.value.endDate === undefined ? {} : { endDate: parsed.value.endDate }),
      };

      await this.projects.save(project);
      await this.audit.record({
        action: 'CREATE',
        entityType: 'Project',
        entityId: project.id,
        actorUserId: session.userId,
        projectId: project.id,
        metadata: { code: project.code, name: project.name },
      });

      return ok(project);
    });
  }
}

export class UpdateProject {
  constructor(
    private readonly projects: ProjectRepository,
    private readonly permissions: PermissionChecker,
    private readonly audit: AuditService,
    private readonly tx: TransactionRunner,
    private readonly clock: Clock,
  ) {}

  async execute(input: unknown, session: Session): Promise<Result<Project, AppError>> {
    const parsed = parseInput(updateProjectSchema, input);
    if (!parsed.ok) return parsed;

    const projectId = asId<'Project'>(parsed.value.projectId) as ProjectId;
    const allowed = this.permissions.ensureForProject(
      session,
      PERMISSION.PROJECTS_MANAGE,
      projectId,
    );
    if (!allowed.ok) return allowed;

    return this.tx.run(async () => {
      const current = await this.projects.findById(projectId);
      if (current === undefined) {
        return err(
          notFoundError(ERROR_CODE.PROJECT_NOT_FOUND, ERROR_MESSAGE[ERROR_CODE.PROJECT_NOT_FOUND]!),
        );
      }

      // Três casos, e os três importam:
      //
      // - **ausente mantém.** Este campo liga a obra ao sistema de compras, e uma tela
      //   que não o envie o apagaria em silêncio — parando as requisições sem que
      //   ninguém tenha pedido isso;
      // - **vazio desliga.** É a pessoa escolhendo "não requisita material" no cadastro,
      //   e precisa valer: opção que não faz nada é pior do que opção ausente;
      // - **preenchido troca o destino.**
      //
      // É por isso que o contrato deste campo não usa `optionalTrimmed`, que juntaria os
      // dois primeiros num só.
      const externalId =
        parsed.value.externalId === undefined
          ? current.externalId
          : parsed.value.externalId === ''
            ? undefined
            : parsed.value.externalId;

      const updated: Project = {
        id: current.id,
        code: current.code,
        name: parsed.value.name,
        timezone: parsed.value.timezone,
        status: parsed.value.status,
        riskHorizonDays: parsed.value.riskHorizonDays,
        requireDistinctInspector: parsed.value.requireDistinctInspector,
        // Ausente mantém (RN-405): a troca de modo é escolha de quem a fez, e nunca efeito de
        // uma tela que não mandou o campo.
        measurementMode: parsed.value.measurementMode ?? current.measurementMode,
        createdAt: current.createdAt,
        updatedAt: this.clock.nowIso(),
        ...(externalId === undefined ? {} : { externalId }),
        ...(parsed.value.client === undefined ? {} : { client: parsed.value.client }),
        ...(parsed.value.address === undefined ? {} : { address: parsed.value.address }),
        ...(parsed.value.technicalManager === undefined
          ? {}
          : { technicalManager: parsed.value.technicalManager }),
        ...(parsed.value.startDate === undefined ? {} : { startDate: parsed.value.startDate }),
        ...(parsed.value.endDate === undefined ? {} : { endDate: parsed.value.endDate }),
        ...(current.workCalendarId === undefined ? {} : { workCalendarId: current.workCalendarId }),
        // A ligação à aprovada não se edita aqui: quem a muda é "aprovar versão".
        ...(current.versionOf === undefined ? {} : { versionOf: current.versionOf }),
        ...(current.versionNumber === undefined ? {} : { versionNumber: current.versionNumber }),
      };

      const changes = diffFields(
        {
          name: current.name,
          client: current.client,
          address: current.address,
          technicalManager: current.technicalManager,
          status: current.status,
          timezone: current.timezone,
          riskHorizonDays: current.riskHorizonDays,
          requireDistinctInspector: current.requireDistinctInspector,
          measurementMode: current.measurementMode,
          startDate: current.startDate,
          endDate: current.endDate,
          externalId: current.externalId,
        },
        {
          name: updated.name,
          client: updated.client,
          address: updated.address,
          technicalManager: updated.technicalManager,
          status: updated.status,
          timezone: updated.timezone,
          riskHorizonDays: updated.riskHorizonDays,
          requireDistinctInspector: updated.requireDistinctInspector,
          measurementMode: updated.measurementMode,
          startDate: updated.startDate,
          endDate: updated.endDate,
          externalId: updated.externalId,
        },
      );

      if (!hasChanges(changes)) return ok(current);

      await this.projects.save(updated);
      await this.audit.record({
        action: 'UPDATE',
        entityType: 'Project',
        entityId: updated.id,
        actorUserId: session.userId,
        projectId: updated.id,
        changes,
      });

      return ok(updated);
    });
  }
}

/**
 * Obra ativa da aplicação.
 *
 * A seleção é conveniência de navegação; o escopo real é verificado em cada caso de uso
 * (RN-132). Ainda assim é validada aqui: selecionar uma obra à qual o perfil não tem
 * acesso não faz sentido nem como navegação.
 */
export class SetActiveProject {
  constructor(
    private readonly settings: SettingsRepository,
    private readonly permissions: PermissionChecker,
  ) {}

  async execute(
    projectId: ProjectId | undefined,
    session: Session,
  ): Promise<Result<ProjectId | undefined, AppError>> {
    if (projectId === undefined) {
      await this.settings.remove(SETTINGS_KEY.ACTIVE_PROJECT_ID);
      return ok(undefined);
    }

    // Só o acesso é validado (RN-132). A existência é do servidor: no modo servidor as
    // obras não moram no banco local, e exigi-las aqui recusaria toda seleção.
    const access = this.permissions.ensureProjectAccess(session, projectId);
    if (!access.ok) return access;

    await this.settings.set(SETTINGS_KEY.ACTIVE_PROJECT_ID, projectId);
    return ok(projectId);
  }
}

export class GetActiveProjectId {
  constructor(private readonly settings: SettingsRepository) {}

  async execute(): Promise<ProjectId | undefined> {
    const stored = await this.settings.get<string>(SETTINGS_KEY.ACTIVE_PROJECT_ID);
    return stored === undefined ? undefined : asId<'Project'>(stored);
  }
}

export class GetProject {
  constructor(
    private readonly projects: ProjectRepository,
    private readonly permissions: PermissionChecker,
  ) {}

  async execute(projectId: ProjectId, session: Session): Promise<Result<Project, AppError>> {
    const access = this.permissions.ensureProjectAccess(session, projectId);
    if (!access.ok) return access;

    const project = await this.projects.findById(projectId);
    if (project === undefined) {
      return err(
        notFoundError(ERROR_CODE.PROJECT_NOT_FOUND, ERROR_MESSAGE[ERROR_CODE.PROJECT_NOT_FOUND]!),
      );
    }
    return ok(project);
  }
}
